import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formatCurrency, formatPercent } from '../../common/SharedFunctions';
import { GridSettingType } from '@apply/constants';
import { LoanStatusType, LoanType } from '../common/LoanConstants';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { BfiDataGrid, Column, Summary, TotalItem } from '../../common/SharedComponents';
import { OnOpenLoanDetail, onActionCellRender, onBorrowerCellRender } from '../common/LoanFunctions';
import { LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { LoanApprovedOrNoteDateColumn } from '../common/components/LoanApprovedOrNoteDateColumn';
import { onLoanTermCellRender } from '../common/functions/onLoanTermCellRender';

const MasterNotesOutstandingTable = ({ gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => {
        criteria.loanTypeId = LoanType.MasterNote;
        criteria.statusId = LoanStatusType.Outstanding;
        return loanApi.getGrid_MasterNotes(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loan);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const customizeTotalValue = useCallback((e) => {
        return e.value === 1 ? e.value + ' Line' : e.value + ' Lines ';
    });

    if (!gridStore) {
        return '';
    }

    const fundedDollarRender = (e) => <p>{formatCurrency(e.data.fundedTotal, 0)}</p>;
    const fundedPercentRender = (e) => (
        <p>{formatPercent(e.data.commitment ? (e.data.fundedTotal ?? 0) / e.data.commitment : 0, 0)}</p>
    );

    return (
        <BfiDataGrid
            id={`mnot_table`}
            gridType={GridSettingType.MasterNotesTable_Outstanding}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onCellDblClick={(e) => OnOpenLoanDetail(e, location)}
        >
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                cellRender={onBorrowerCellRender}
                name='borrowerEntity'
                minWidth={120}
            />

            <Column
                dataField='borrowerCity'
                caption='Borr. City'
                width={90}
            />
            <Column
                dataField='borrowerState'
                caption='Borr. State'
                width={70}
            />

            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={80}
            />
            <Column
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            {LoanCommitOrApprovalColumn({ statusId: LoanStatusType.Outstanding })}
            {LoanApprovedOrNoteDateColumn({ statusId: LoanStatusType.Outstanding })}
            <Column
                dataField='monthsSinceClosing'
                caption='Mnths'
                width={60}
                alignment={'left'}
            />
            <Column
                dataField='origMaturityDate'
                caption='Maturity'
                dataType='date'
                width={90}
            />
            <Column
                dataField='initialTermMonths'
                caption='Term'
                dataType='string'
                width={60}
                alignment={'left'}
                cellRender={onLoanTermCellRender}
            />
            <Column
                dataField='fundedTotal'
                caption='Funded'
                dataType='number'
                format='currency'
                width={90}
                alignment={'left'}
                cellRender={fundedDollarRender}
            />
            <Column
                caption='Funded'
                dataType='number'
                width={70}
                alignment={'left'}
                cellRender={fundedPercentRender}
                allowSorting={false}
                cssClass='no-sort-header'
            />
            <Column
                dataField='interestRate'
                caption='Rate'
                dataType='number'
                format={{ type: 'percent', precision: 2 }}
                width={90}
                alignment={'left'}
            />
            <Column
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={110}
            />
            <Column
                dataField='ownerName'
                caption='User'
                dataType='string'
                width={110}
            />
            <Column
                dataField='broker'
                dataType='string'
                width={110}
            />
            <Column
                alignment='center'
                cellRender={onActionCellRender}
                width={50}
                minWidth={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                fixed={true}
                fixedPosition={'right'}
            />
            <Summary>
                <TotalItem
                    showInColumn='borrowerEntity'
                    customizeText={customizeTotalValue}
                />
                <TotalItem
                    column='commitment'
                    valueFormat='currency'
                />
            </Summary>
        </BfiDataGrid>
    );
};

export default MasterNotesOutstandingTable;
